import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { loadStripe } from '@stripe/stripe-js';
import { isInAppBrowser, inAppBrowserName, escapeInstructions } from '../utils/inAppBrowser';
import './PayInvoice.css';

function PayInvoice() {
  const { invoiceNumber } = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('token');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);

  // Stripe.js is only loaded when this page mounts (not on every page of the app).
  // useMemo keeps the promise stable across re-renders.
  //
  // Stripe.js can fail to load for environmental reasons that are NOT bugs in
  // our code: ad blockers, privacy extensions (Brave/Ghostery/uBlock), strict
  // browser settings, corporate firewalls, or transient DNS hiccups can all
  // block js.stripe.com. We catch the rejection here and resolve to null so
  // we can surface a helpful error message inside handlePay() instead of
  // throwing a generic "Payment failed" message.
  const stripePromise = useMemo(() => {
    const key = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    if (!key) {
      console.error('REACT_APP_STRIPE_PUBLIC_KEY is not configured');
      return Promise.resolve(null);
    }
    return loadStripe(key).catch((err) => {
      console.warn('Stripe.js failed to load:', err?.message || err);
      return null;
    });
  }, []);

  // Detected once on mount — the user agent cannot change mid-session, and
  // re-evaluating on every render would run this on each keystroke.
  const inApp = useMemo(() => isInAppBrowser(), []);
  const hostName = useMemo(() => (inApp ? inAppBrowserName() : null), [inApp]);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 4000);
    } catch (e) {
      // Clipboard API is blocked in some webviews — select the URL instead so
      // there is still a way out.
      window.prompt('Copy this link and open it in your browser:', window.location.href);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, [invoiceNumber]);

  useEffect(() => {
    if (invoice && invoice._id) {
      setError('');
    }
  }, [invoice]);

  const fetchInvoice = async () => {
    try {
      // api instance automatically attaches Bearer token for logged-in users
      // accessToken from ?token= query param provides access for email links
      const response = await api.get(`/invoice/${invoiceNumber}${accessToken ? `?token=${accessToken}` : ''}`);
      
      const invoiceData = response.data.data;
      
      if (invoiceData) {
        setInvoice(invoiceData);
        setError('');
      } else {
        setError('Invoice data is missing');
      }
    } catch (err) {
      console.error('Invoice fetch error:', err);
      
      if (err.response?.status === 404) {
        setError('Invoice not found. It may have been deleted or the link is invalid.');
      } else if (err.response?.status === 403) {
        setError('Access denied. Please use the secure invoice link from your email.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Unable to load invoice. Please check the link and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    try {
      setPaying(true);
      
      const response = await api.post('/payment/create-invoice-payment', {
        invoiceId: invoice._id,
        invoiceNumber: invoice.invoiceNumber,
        // Required since Pack B unless signed in as the invoice's owner.
        token: accessToken || undefined
      });

      const stripe = await stripePromise;

      if (!stripe) {
        // Stripe.js failed to load — usually an ad blocker or privacy extension
        // blocking js.stripe.com. Give the user actionable guidance.
        setError('We couldn\'t load our payment processor. This is usually caused by an ad blocker or privacy extension blocking js.stripe.com. Please disable extensions for this site, try a different browser, or email info@utahrealtyphotos.com to pay another way.');
        setPaying(false);
        return;
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.sessionId
      });

      if (error) {
        setError(error.message);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.message || 'Payment failed. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="pay-invoice-page">
        <div className="loading">Loading invoice...</div>
      </div>
    );
  }

  if (!invoice || !invoice._id) {
    return (
      <div className="pay-invoice-page">
        <div className="error-message">
          <h4>Invoice Not Found</h4>
          <p>{error || 'This invoice may have been paid or does not exist.'}</p>
        </div>
      </div>
    );
  }

  // Show paid badge but still render the full invoice
  const isPaid = invoice.status === 'paid';
  const isPartiallyPaid = invoice.status === 'partially_paid' || (invoice.amountPaid > 0 && invoice.amountPaid < invoice.total);
  const remainingBalance = invoice.balanceDue || Math.round((invoice.total - (invoice.amountPaid || 0)) * 100) / 100;

  return (
    <div className="pay-invoice-page">
      <div className="invoice-container">
        {inApp && (
          /* An in-app webview is a poor place to take a card payment: storage is
             partitioned so Stripe's session can be lost on redirect, Apple Pay
             and Google Pay are unavailable, and some hosts drop the return URL
             so a successful payment never shows as paid. None of that throws an
             error — it just converts worse. Say so before they try. */
          <div className="inapp-notice" role="status">
            <strong>Opening in your browser will work better.</strong>
            <p>
              {hostName ? `Links opened inside ${hostName} ` : 'Links opened inside another app '}
              can block saved cards and Apple Pay. {escapeInstructions()}
            </p>
            <button type="button" onClick={handleCopyLink} className="inapp-copy">
              {copied ? 'Link copied' : 'Copy payment link'}
            </button>
          </div>
        )}
        <div className="invoice-header">
          <h1>Invoice #{invoice.invoiceNumber}</h1>
          <div className="invoice-date">
            Due: {new Date(invoice.dueDate).toLocaleDateString()}
          </div>
        </div>

        <div className="invoice-details">
          <div className="detail-section">
            <h3>Bill To:</h3>
            <p>{invoice.clientName}</p>
            <p>{invoice.clientEmail}</p>
          </div>

          <div className="detail-section">
            <h3>Service:</h3>
            <p>{invoice.description}</p>
          </div>

          <div className="invoice-items">
            <h3>Items:</h3>
            <table className="items-table">
              <thead>
                <tr>
                  <th style={{textAlign:'left'}}>Description</th>
                  <th style={{textAlign:'center'}}>Quantity</th>
                  <th style={{textAlign:'right'}}>Price</th>
                  <th style={{textAlign:'right'}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.lineItems || invoice.items || []).map((item, index) => (
                  <tr key={index} className={item.total < 0 ? 'discount-row' : ''}>
                    <td>{item.description}</td>
                    <td style={{textAlign:'center'}}>{item.quantity || 1}</td>
                    <td style={{textAlign:'right'}}>
                      {item.unitPrice < 0 ? '-' : ''}${Math.abs(item.unitPrice || item.price || item.amount || 0).toFixed(2)}
                    </td>
                    <td style={{textAlign:'right'}}>
                      {item.total < 0 ? '-' : ''}${Math.abs(item.total || item.amount || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="invoice-total">
            <div className="total-row">
              <span>Subtotal:</span>
              <span>${(invoice.subtotal || invoice.totalPrice || invoice.total || 0).toFixed(2)}</span>
            </div>
            {invoice.tax > 0 && (
              <div className="total-row">
                <span>Tax {invoice.taxRate ? `(${invoice.taxRate}%)` : ''}:</span>
                <span>${(invoice.tax || 0).toFixed(2)}</span>
              </div>
            )}
            <div className="total-row total-amount">
              <span><strong>Total:</strong></span>
              <span><strong>${(invoice.total || invoice.totalPrice || 0).toFixed(2)}</strong></span>
            </div>
            {invoice.depositAmount > 0 && (
              <>
                <div className="total-row" style={{marginTop: '20px', paddingTop: '15px', borderTop: '2px solid #e5e7eb'}}>
                  <span>Deposit Due Now {invoice.depositRate ? `(${invoice.depositRate}%)` : ''}:</span>
                  <span style={{color: '#2563eb', fontWeight: '600'}}>${(invoice.depositAmount || 0).toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span>Balance Due Later:</span>
                  <span>${(invoice.balanceDue || 0).toFixed(2)}</span>
                </div>
              </>
            )}

            {/* Credit prompt at the highest-intent moment there is: someone
                looking at what a shoot costs. This page is token-accessed, so
                it reaches clients who may never open the portal — and it is
                the only surface where the saving is concrete rather than
                abstract. Deliberately quiet: an invoice is not the place for a
                hard sell, and anything louder would read as a shakedown. */}
            <div style={{
              marginTop: '18px', paddingTop: '14px', borderTop: '1px solid #e5e7eb',
              fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.6
            }}>
              Shooting more listings this year?{' '}
              <a href="/credit" style={{ color: '#2563eb', fontWeight: 600 }}>
                Prepaid credit
              </a>{' '}
              takes up to 16% off invoices like this one &mdash; no monthly commitment, never expires.
            </div>
          </div>
        </div>

        <div className="payment-section">
          {isPaid && (
            <div style={{
              background: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <p style={{ color: '#155724', margin: 0, fontWeight: '700', fontSize: '18px' }}>
                ✓ Paid in Full
              </p>
              <p style={{ color: '#155724', margin: '5px 0 0', fontSize: '14px' }}>
                Paid on {new Date(invoice.paidAt).toLocaleDateString()}
              </p>
            </div>
          )}
          {isPartiallyPaid && (
            <div style={{
              background: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '8px',
              padding: '15px 20px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <p style={{ color: '#155724', margin: 0, fontWeight: '600' }}>
                Deposit of ${(invoice.amountPaid || 0).toFixed(2)} received — thank you!
              </p>
              <p style={{ color: '#155724', margin: '5px 0 0', fontSize: '14px' }}>
                Remaining balance: ${remainingBalance.toFixed(2)}
              </p>
            </div>
          )}
          {!isPaid && (
            <button 
              className="btn-pay" 
              onClick={handlePayment}
              disabled={paying}
            >
              {paying ? 'Processing...' : 
               isPartiallyPaid
                 ? `Pay Remaining Balance $${remainingBalance.toFixed(2)}`
                 : invoice.depositAmount > 0 
                   ? `Pay Deposit $${(invoice.depositAmount || 0).toFixed(2)}` 
                   : `Pay $${(invoice.total || invoice.totalPrice || 0).toFixed(2)}`
              }
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PayInvoice;
